import React, { memo } from "react";
import { CustomPropsJson } from "./types";
import { z } from "zod";
import { Control, FieldValues, FormState, UseFormRegister, useFormContext, useWatch } from "react-hook-form";
import SelectInput from "./inputs/SelectInput";
import { elementCreateUpdateSchema } from "@ept/shared/src/schemas";
import CheckboxInput from "./inputs/CheckboxInput";
import FileUploadInput from "./inputs/FileUploadInput";
import DatePickerInput from "./inputs/DatePickerInput";
import { cn } from "@/lib/utils";
import { getDesktopColSpan, getMobileColSpan, getTabletColSpan } from "./utils/form-columns";
import { replaceMarkers } from "@/utils/genericUtils";
import TableInput from "./inputs/TableInput";
import LocationPicker from "./inputs/LocationPicker";
import { FormField, FormItem } from "../ui/form";
import LinkField from "./inputs/LinkField";
import InputWrapper from "./inputs/InputWrapper";
import TextareaWrapper from "./inputs/TextAreaWrapper";
import TextBox from "./inputs/TextBox";
import { isElementNotEmpty } from "@ept/shared/src/validators";
import LocationViewer from "./inputs/LocationViewer";
type SectionInputProps = {
  element: z.infer<typeof elementCreateUpdateSchema> & {
    id: string;
    customPropsJson: CustomPropsJson | null;
  };
  disabled?: boolean;
  isPreviewOnly?: boolean;
  replaceMarkersData?: Record<string, string | undefined>;
  institutionSlug: string | undefined;
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues, any>;
  formState: FormState<FieldValues>;
  readonly?: boolean;
};
function SectionInput({
  element,
  disabled,
  replaceMarkersData,
  isPreviewOnly,
  institutionSlug,
  register,
  control,
  formState,
  readonly
}: SectionInputProps) {
  const customPropsJson = element.customPropsJson;
  const value = useWatch({
    name: element.id
  });
  const renderField = () => {
    switch (element.type) {
      case "TEXT":
        return <TextBox text={replaceMarkers(element.extraAttributes?.customDescription || element.displayName || "", {
          institution: replaceMarkersData?.institution
        })} textStyle={element.customPropsJson?.displayStyle} alignmentStyle={element.customPropsJson?.alignmentStyle} />;
      case "TEXT_BOX":
        return <FormField control={control} name={element.id} render={({
          field
        }) => <FormItem>
                <InputWrapper {...field} placeholder={element.placeholder || element.displayName || ""} disabled={disabled || isPreviewOnly} labelText={element.displayName} isElementTouched={Boolean(formState.errors[element.id!])} isElementValid={isElementNotEmpty(field.value, element.type) && !formState.errors[element.id!]} />
              </FormItem>} />;
      case "LOCATION":
        {
          const zoneLimitValidator = element.validators.find(validator => validator.validator.type === "ZONE_LIMIT");
          const getLocationGeometryType = (): {
            type: "dynamic";
            institutionSlug: string | undefined;
          } | {
            type: "static";
            geometryId: string | undefined;
          } | {
            type: undefined;
            institutionSlug: string | undefined;
          } => {
            if (!zoneLimitValidator) {
              return {
                institutionSlug,
                type: undefined
              };
            }
            if (zoneLimitValidator?.validator.options?.value === "DYNAMIC" || zoneLimitValidator?.validator.options?.value === "_dynamic") {
              return {
                type: "dynamic",
                institutionSlug
              };
            } else {
              return {
                type: "static",
                geometryId: zoneLimitValidator.validator.options?.value
              }; //the geometry id
            }
          };
          const mapGeometry = getLocationGeometryType();
          if (disabled) {
            return <LocationViewer selectedLocation={value ? value.split(",").map((coord: string) => Number(coord)) : undefined} title={element.displayName || "Alege locatia"} />;
          }
          return <FormField name={element.id} control={control} render={({
            field
          }) => <FormItem>
                <LocationPicker disabled={disabled} initialCoordinates={element.defaultValue ? element.defaultValue.split(",").map(coord => Number(coord)) : undefined} type={mapGeometry?.type} locationPolygonSlug={mapGeometry.type !== "static" ? mapGeometry.institutionSlug : undefined} geometryId={mapGeometry?.type === "static" ? mapGeometry.geometryId : undefined} onChange={newCoordinates => field.onChange(newCoordinates.join(","))} customErrorMessage={zoneLimitValidator?.validator.customErrorMessage || ""} title={element.displayName || "Alege locatia"} />
              </FormItem>} />;
        }
      case "NUMBER":
        return <FormField control={control} name={element.id} render={({
          field
        }) => <FormItem>
                <InputWrapper {...field} type="number" placeholder={element.placeholder || element.displayName || ""} disabled={disabled} labelText={element.displayName} isElementValid={isElementNotEmpty(field.value, element.type) && !formState.errors[element.id!]} />
              </FormItem>} />;
      case "TEXT_AREA":
        return <FormField control={control} name={element.id} render={({
          field
        }) => <FormItem>
                <TextareaWrapper {...field} placeholder={element.placeholder || element.displayName || ""} disabled={disabled} labelText={element.displayName} isElementTouched={Boolean(formState.errors[element.id!])} isElementValid={isElementNotEmpty(field.value, element.type) && !formState.errors[element.id!]} />
              </FormItem>} />;
      case "LINK":
        return <LinkField title={element.extraAttributes?.customDescription || element.displayName} url={element.defaultValue || ""} textStyle={element.customPropsJson?.displayStyle} buttonTitle={element.placeholder ?? undefined} />;
      case "SELECT":
        return <SelectInput elementId={element.id} placeholder={element.placeholder ?? element.displayName} disabled={disabled} options={element.options?.sort((a, b) => a.displayOrder - b.displayOrder).map(option => ({
          ...option,
          id: option.id!
        })) || []} isElementTouched={Boolean(formState.errors[element.id!])} isElementValid={isElementNotEmpty(value, element.type) && !formState.errors[element.id!]} />;
      case "CHECKBOX":
        return <CheckboxInput disabled={disabled} elementId={element.id} label={replaceMarkers(element.extraAttributes?.customDescription || element.displayName || "", {
          institution: replaceMarkersData?.institution
        })} />;
      case "FILE":
      case "FILE_MULTIPLE":
        {
          return isPreviewOnly && value && value.length ? <div className="flex flex-col gap-2">
            {value.map((file: any) => {
              const fileNameFormatted = file.name?.replace(/^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-/i, "");
              return <div className="flex flex-col gap-2" key={value.id}>
                  <p className="text-sm text-gray-500">{element.displayName}</p>
                  {readonly ? <a className="text-blue-500 underline" href={file.url} target="_blank" rel="noreferrer">
                      {fileNameFormatted}
                    </a> : <a className="text-blue-500 underline" href={file.url || "#"} target="_blank" rel="noreferrer">
                      {fileNameFormatted}
                    </a>}
                </div>;
            })}
          </div> : <FileUploadInput elementId={element.id} disabled={disabled || isPreviewOnly} multiple={element.type === "FILE_MULTIPLE"} labelText={element.displayName} />;
        }
      case "DATE":
        return <DatePickerInput elementId={element.id} label={element.displayName} disabled={disabled}
        //@ts-expect-error as type is inherited from zod, it thinks it won't fit with the type of the extraAttributes from the element props
        elementExtraAttributes={element.extraAttributes} />;
      case "TABLE":
        return <TableInput elementId={element.id} extraAttributes={element.extraAttributes} label={element.displayName} disabled={disabled} />;
      case "GAP":
        return null;
    }
  };
  const getGridProps = () => {
    return customPropsJson?.gridProps ? `${getMobileColSpan(Number(customPropsJson.gridProps.sm))} ${getTabletColSpan(Number(customPropsJson.gridProps.md))} ${getDesktopColSpan(Number(customPropsJson.gridProps.lg))}` : "col-span-12";
  };
  return <div className={cn("pb-3", getGridProps())} id={element.id!} data-sentry-component="SectionInput" data-sentry-source-file="SectionInput.tsx">
      <div className="relative">{renderField()}</div>
      <p className="px-1 pt-1 text-xs text-red-500">
        {formState.errors?.[element.id!]?.message?.toString()}
      </p>
    </div>;
}
const MemoizedInput = memo(SectionInput, (prevProps, nextProps) => prevProps.formState.isDirty === nextProps.formState.isDirty && prevProps.formState.errors === nextProps.formState.errors);
const SectionInputWrapper = (props: Omit<SectionInputProps, "control" | "formState" | "register">) => {
  const form = useFormContext();
  return <MemoizedInput {...props} control={form.control} formState={form.formState} register={form.register} data-sentry-element="MemoizedInput" data-sentry-component="SectionInputWrapper" data-sentry-source-file="SectionInput.tsx" />;
};
export default SectionInputWrapper;